/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  message,
  Table,
  Button,
  Input,
  Tag,
  Modal,
  Alert,
  Dropdown,
  Spin,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import Header from "../layouts/Header";
import Sidebar from "../layouts/Sidebar";
import { boatApi } from "../../apis";
import { hasPermission } from "../../utils/permissions";

const { Search } = Input;

// ─── Decks List ───────────────────────────────────────────────────────────────
export const DecksList = () => {
  const history = useHistory();
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [decks, setDecks] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ page: 1, limit: 10 });
  const [deleteModal, setDeleteModal] = useState({
    visible: false,
    id: null,
    name: "",
  });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [hiddenDeckIds, setHiddenDeckIds] = useState([]);
  const [userPermissions, setUserPermissions] = useState([]);
  const [isCheckingPermission, setIsCheckingPermission] = useState(true);

  // Load user permissions
  useEffect(() => {
    const loadUserPermissions = () => {
      try {
        const userStr = localStorage.getItem("user");
        if (!userStr) {
          setUserPermissions([]);
          setIsCheckingPermission(false);
          return;
        }

        const userData = JSON.parse(userStr);

        // Extract permissions - handle both string array and object array
        let permissions = [];

        if (userData.permissions && Array.isArray(userData.permissions)) {
          permissions = userData.permissions;
        } else if (userData.role && userData.role.permissions) {
          permissions = userData.role.permissions;
        }

        // Normalize to strings
        const normalizedPermissions = permissions.map((p) =>
          typeof p === "string" ? p : p.name,
        );

        setUserPermissions(normalizedPermissions);
        console.log("DecksList - User permissions:", normalizedPermissions);
      } catch (error) {
        console.error("Error loading user permissions:", error);
        setUserPermissions([]);
      } finally {
        setIsCheckingPermission(false);
      }
    };

    loadUserPermissions();
  }, []);

  // Check permissions using the standardized hasPermission utility
  const canViewDecks = useMemo(() => {
    return hasPermission(userPermissions, "boat.view");
  }, [userPermissions]);

  const canCreateDecks = useMemo(() => {
    return hasPermission(userPermissions, "boat.create");
  }, [userPermissions]);

  const canEditDecks = useMemo(() => {
    return hasPermission(userPermissions, "boat.edit");
  }, [userPermissions]);

  const canDeleteDecks = useMemo(() => {
    return hasPermission(userPermissions, "boat.delete");
  }, [userPermissions]);

  // Combined manage permission
  const canManageDecks = useMemo(() => {
    return canCreateDecks || canEditDecks || canDeleteDecks;
  }, [canCreateDecks, canEditDecks, canDeleteDecks]);

  useEffect(() => {
    if (!isCheckingPermission && canViewDecks) {
      fetchDecks();
    }
  }, [filters, search, canViewDecks, isCheckingPermission]);

  const fetchDecks = async () => {
    try {
      setLoading(true);
      const response = await boatApi.getAllDecks();
      const visibleDecks = (response || [])
        .filter((deck) => !hiddenDeckIds.includes(deck.id))
        .map((deck, index) => ({ ...deck, key: deck.id || index }));

      setDecks(visibleDecks);
      setTotalItems(visibleDecks.length);
    } catch (error) {
      console.error("Error fetching decks:", error);
      if (error.response?.status === 403) {
        message.error("You don't have permission to view decks");
      } else {
        message.error("Failed to load decks");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
    setFilters((f) => ({ ...f, page: 1 }));
  };

  // Filter decks based on search
  const filteredDecks = useMemo(() => {
    if (!search.trim()) return decks;

    const query = search.toLowerCase().trim();
    return decks.filter((deck) => {
      return (
        deck.name?.toLowerCase().includes(query) ||
        deck.capacity?.toString().includes(query) ||
        deck.defaultAdultPrice?.toString().includes(query) ||
        deck.defaultChildPrice?.toString().includes(query)
      );
    });
  }, [decks, search]);

  // Paginate filtered decks
  const paginatedDecks = useMemo(() => {
    const start = (filters.page - 1) * filters.limit;
    const end = start + filters.limit;
    return filteredDecks.slice(start, end);
  }, [filteredDecks, filters.page, filters.limit]);

  const columns = [
    {
      title: "Deck Name",
      dataIndex: "name",
      key: "name",
      render: (name) => <strong>{name}</strong>,
    },
    {
      title: "Capacity",
      dataIndex: "capacity",
      key: "capacity",
      render: (v) => `${v} seats`,
    },
    {
      title: "Adult Price",
      dataIndex: "defaultAdultPrice",
      key: "adultPrice",
      render: (v) => `EGP ${Number(v).toLocaleString()}`,
    },
    {
      title: "Child Price",
      dataIndex: "defaultChildPrice",
      key: "childPrice",
      render: (v) => `EGP ${Number(v).toLocaleString()}`,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "status",
      render: (v) => (
        <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag>
      ),
    },
    // Actions column - only show if user has any management permissions
    ...(canManageDecks
      ? [
          {
            title: "Actions",
            key: "actions",
            width: 80,
            render: (_, record) => {
              const items = [];

              // Edit - only if user has edit permission
              if (canEditDecks) {
                items.push({
                  key: "edit",
                  label: "Edit",
                  icon: <EditOutlined />,
                  onClick: () => history.push(`/boat/decks/${record.id}/edit`),
                });
              }

              if (items.length === 0) return null;

              return (
                <Dropdown
                  menu={{ items }}
                  placement="bottomRight"
                  trigger={["click"]}
                >
                  <Button type="text" icon={<MoreOutlined />} />
                </Dropdown>
              );
            },
          },
        ]
      : []),
  ];

  // Show loading while checking permissions
  if (isCheckingPermission) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <div className="text-center py-5">
              <Spin size="large" />
              <p className="mt-2">Checking permissions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Permission check for viewing the page
  if (!canViewDecks) {
    return (
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => setMenu(!menu)} />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content container-fluid">
            <Alert
              type="error"
              message="Access Denied"
              description={
                <div>
                  <p>You don't have permission to view boat decks.</p>
                  <p className="mt-2">
                    <strong>Required permission:</strong> boat.view
                  </p>
                  <p className="mt-2">
                    <strong>Your permissions:</strong>{" "}
                    {userPermissions.length > 0
                      ? userPermissions.join(", ")
                      : "None"}
                  </p>
                </div>
              }
              showIcon
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0">
            <div className="card-body">
              <div className="page-header">
                <div className="content-page-header d-flex justify-content-between align-items-center">
                  <div>
                    <h5>Boat Decks Management</h5>
                    <p className="text-muted mb-0 mt-1">
                      {canManageDecks
                        ? `You have ${canCreateDecks ? "create, " : ""}${
                            canEditDecks ? "edit, " : ""
                          }${
                            canDeleteDecks ? "delete" : ""
                          } access to manage decks`
                        : "You can view decks but cannot modify them"}
                    </p>
                  </div>
                  {/* Add Deck button - only visible to users with create permission */}
                  {/* {canCreateDecks && (
                    <div className="list-btn">
                      <Button
                        type="primary"
                        style={{
                          backgroundColor: "#7B4D12",
                          borderColor: "#7B4D12",
                        }}
                        icon={<PlusOutlined />}
                        onClick={() => history.push("/boat/decks/add")}
                      >
                        Add Deck
                      </Button>
                    </div>
                  )} */}
                </div>
              </div>

              {/* View Only Mode Alert */}
              {!canManageDecks && canViewDecks && (
                <Alert
                  message="View Only Mode"
                  description="You have view-only access to decks. To create, edit, or delete decks, please contact an administrator."
                  type="info"
                  showIcon
                  className="mb-4"
                />
              )}

              <div className="row mb-4">
                <div className="col-md-4">
                  <Search
                    placeholder="Search decks by name, capacity, or prices…"
                    onSearch={handleSearch}
                    enterButton={<SearchOutlined />}
                    allowClear
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>

              {filteredDecks.length === 0 && !loading ? (
                <div className="text-center py-5">
                  <p className="text-muted">
                    {search ? "No decks match your search" : "No decks found"}
                  </p>
                  {canCreateDecks && !search && (
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: "#7B4D12",
                        borderColor: "#7B4D12",
                      }}
                      icon={<PlusOutlined />}
                      onClick={() => history.push("/boat/decks/add")}
                    >
                      Create your first deck
                    </Button>
                  )}
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={paginatedDecks}
                  rowKey="id"
                  loading={loading}
                  pagination={{
                    current: filters.page,
                    pageSize: filters.limit,
                    total: filteredDecks.length,
                    showSizeChanger: true,
                    showTotal: (t, r) => `${r[0]}-${r[1]} of ${t}`,
                  }}
                  onChange={(p) =>
                    setFilters((f) => ({
                      ...f,
                      page: p.current,
                      limit: p.pageSize,
                    }))
                  }
                  scroll={{ x: true }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DecksList;

// ─── Add Deck ─────────────────────────────────────────────────────────────────
export const AddDeck = () => {
  const history = useHistory();
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    capacity: 50,
    defaultAdultPrice: 500,
    defaultChildPrice: 250,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return message.error("Name is required");
    try {
      setLoading(true);
      await boatApi.createDeck(form);
      message.success("Deck created");
      history.push("/boat/decks");
    } catch (e) {
      message.error(e.response?.data?.message || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0">
            <div className="card-body">
              <div className="page-header">
                <div className="content-page-header">
                  <h5>Add Deck</h5>
                  <button
                    type="button"
                    className="btn btn-primary cancel me-2"
                    onClick={() => history.goBack()}
                  >
                    Back
                  </button>
                </div>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group-item">
                  <div className="row">
                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Deck Name <span className="text-danger">*</span>
                        </label>
                        <input
                          className="form-control"
                          placeholder="e.g. Upper Deck"
                          value={form.name}
                          onChange={(e) =>
                            setForm((f) => ({ ...f, name: e.target.value }))
                          }
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>
                    <div className="col-lg-4 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Capacity (seats){" "}
                          <span className="text-danger">*</span>
                        </label>
                        <InputNumber
                          style={{ width: "100%" }}
                          min={1}
                          value={form.capacity}
                          onChange={(v) =>
                            setForm((f) => ({ ...f, capacity: v }))
                          }
                          disabled={loading}
                        />
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Adult Price (EGP){" "}
                          <span className="text-danger">*</span>
                        </label>
                        <InputNumber
                          style={{ width: "100%" }}
                          min={1}
                          value={form.defaultAdultPrice}
                          onChange={(v) =>
                            setForm((f) => ({ ...f, defaultAdultPrice: v }))
                          }
                          disabled={loading}
                        />
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="input-block mb-3">
                        <label>
                          Child Price (EGP){" "}
                          <span className="text-danger">*</span>
                        </label>
                        <InputNumber
                          style={{ width: "100%" }}
                          min={1}
                          value={form.defaultChildPrice}
                          onChange={(v) =>
                            setForm((f) => ({ ...f, defaultChildPrice: v }))
                          }
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="form-group-item border-0">
                  <div className="text-end">
                    <button
                      type="button"
                      className="btn btn-primary cancel me-2"
                      onClick={() => history.goBack()}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Saving…
                        </>
                      ) : (
                        "Save Deck"
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Schedules List ───────────────────────────────────────────────────────────
export const SchedulesList = () => {
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [tripId, setTripId] = useState("");

  // Grab tripId from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tid = params.get("tripId");
    if (tid) {
      setTripId(tid);
      fetchSchedules(tid);
    }
  }, []);

  const fetchSchedules = async (tid) => {
    if (!tid) return;
    try {
      setLoading(true);
      const r = await schedulesApi.getByTrip(tid, { limit: 200 });
      setSchedules(r.data || []);
    } catch {
      message.error("Failed to load schedules");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      await schedulesApi.cancel(id);
      message.success("Date cancelled — no new bookings will be accepted");
      fetchSchedules(tripId);
    } catch (e) {
      message.error(e.response?.data?.message || "Failed");
    }
  };

  const handleRestore = async (id) => {
    try {
      await schedulesApi.restore(id);
      message.success("Date restored");
      fetchSchedules(tripId);
    } catch (e) {
      message.error(e.response?.data?.message || "Failed");
    }
  };

  const columns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (d) => <strong>{d}</strong>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => (
        <Tag color={s === "active" ? "green" : "red"}>{s?.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Overrides",
      dataIndex: "deckPricings",
      key: "overrides",
      render: (p) =>
        (p || []).length > 0 ? (
          <Tag color="orange">{p.length} override(s)</Tag>
        ) : (
          <span className="text-muted">None</span>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, r) =>
        r.status === "active" ? (
          <Button size="small" danger onClick={() => handleCancel(r.id)}>
            Cancel Date
          </Button>
        ) : (
          <Button size="small" onClick={() => handleRestore(r.id)}>
            Restore
          </Button>
        ),
    },
  ];

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0">
            <div className="card-body">
              <div className="page-header">
                <div className="content-page-header">
                  <h5>Schedule Management</h5>
                </div>
              </div>
              {!tripId && (
                <Alert
                  message="Select a trip from the Trips page to manage its schedule dates."
                  type="info"
                  showIcon
                />
              )}
              {tripId && (
                <Table
                  columns={columns}
                  dataSource={schedules}
                  rowKey="id"
                  loading={loading}
                  pagination={{ pageSize: 50, showSizeChanger: true }}
                  scroll={{ x: true }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Payments List ────────────────────────────────────────────────────────────
export const PaymentsList = () => {
  const [menu, setMenu] = useState(false);
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [reservationId, setReservationId] = useState("");

  const STATUS_COLORS = {
    success: "green",
    failed: "red",
    pending: "orange",
    refunded: "volcano",
  };

  const handleSearch = async () => {
    if (!reservationId.trim()) return message.error("Enter a reservation ID");
    try {
      setLoading(true);
      const r = await paymentsApi.getByReservation(reservationId.trim());
      setPayments(Array.isArray(r) ? r : []);
    } catch (e) {
      message.error(e.response?.data?.message || "Not found");
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSimulate = async (paymentId) => {
    try {
      const r = await paymentsApi.simulateSuccess(paymentId);
      message.success(r.message || "Payment simulated as success");
      handleSearch();
    } catch (e) {
      message.error(e.response?.data?.message || "Failed");
    }
  };

  const columns = [
    {
      title: "Payment ID",
      dataIndex: "id",
      key: "id",
      render: (id) => <code>{id?.slice(0, 8)}…</code>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (v) => `EGP ${Number(v).toLocaleString()}`,
    },
    {
      title: "Method",
      dataIndex: "method",
      key: "method",
      render: (m) => <Tag>{m?.toUpperCase()}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => <Tag color={STATUS_COLORS[s]}>{s?.toUpperCase()}</Tag>,
    },
    {
      title: "Transaction ID",
      dataIndex: "transactionId",
      key: "txn",
      render: (t) => t || "-",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "created",
      render: (d) => new Date(d).toLocaleString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, r) =>
        r.status === "pending" && (
          <Button
            size="small"
            type="primary"
            style={{ backgroundColor: "#7B4D12", borderColor: "#7B4D12" }}
            onClick={() => handleSimulate(r.id)}
          >
            Simulate Success
          </Button>
        ),
    },
  ];

  return (
    <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
      <Header onMenuClick={() => setMenu(!menu)} />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="card mb-0">
            <div className="card-body">
              <div className="page-header">
                <div className="content-page-header">
                  <h5>Payment Records</h5>
                </div>
              </div>
              <div className="row mb-4">
                <div className="col-md-5">
                  <div className="input-group">
                    <input
                      className="form-control"
                      placeholder="Enter Reservation ID (UUID)…"
                      value={reservationId}
                      onChange={(e) => setReservationId(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    />
                    <Button
                      type="primary"
                      style={{
                        backgroundColor: "#7B4D12",
                        borderColor: "#7B4D12",
                      }}
                      onClick={handleSearch}
                      loading={loading}
                    >
                      Search
                    </Button>
                  </div>
                </div>
              </div>
              <Table
                columns={columns}
                dataSource={payments}
                rowKey="id"
                loading={loading}
                scroll={{ x: true }}
                locale={{
                  emptyText: "Search by reservation ID to view payments",
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
